import useMedia from "utils/hooks/useMedia";
import React, { ReactNode, useContext, useState } from "react";
import ArrowLeftOutlined from "@ant-design/icons/ArrowLeftOutlined";
import MenuOutlined from "@ant-design/icons/MenuOutlined";
import Layout from "antd/es/layout";
import PageTitle from "common/Home/PageTitle";
import {
  AFFILIATION_PATH,
  BASE_BILLING_SETTINGS_PATH,
  BOTTOM_NAVIGATOR,
  IS_MOBILE_WEBVIEW,
  MOBILE_MENU,
  MOBILE_SEARCH,
  SETTINGS_PATH,
} from "common/constants";
import NotificationsButton from "features/notifications/NotificationsButton";
import DisplayCurrencyToggle from "common/Home/Header/DisplayCurrencyToggle";
import { MobileFilters } from "common/Home/Header/MobileFilters";
import styled from "styled-components";
import MobileSearch from "common/Home/Header/MobileSearch";
import { PageWrapperContext } from "../PageWrapper.context";
import UserInfo from "features/userInfo/UserInfo";
import TopSymbolsList from "features/watchlist/TopSymbols/TopSymbolsList";
import cornixLogo from "images/svg_logo.svg";
import cornixWhiteLogo from "images/svg_white_logo.svg";
import { useSelector } from "react-redux";
import { selectIsDarkMode, selectWorkspace } from "@shared/slices/common.slice";
import { useBottomNavigator, useIsOnboarding } from "utils/hooks/misc";
import { AuthUser, WorkspaceType } from "@shared/types";
import { HEADER_ICON_SIZE } from "@shared/common/Themes/constants";
import WorkspaceSwitch from "common/Home/Header/WorkspaceSwitch";
import Flex from "common/Boxes/Flex";
import { HEADER_HEIGHT, MOBILE_HEADER_HEIGHT } from "common/Home/constants";
import DemoTag from "common/Tags/DemoTag";
import UpgradeButton from "common/Home/Header/UpgradeButton";
import useRoutesMatch from "utils/hooks/useRoutesMatch";
import GroupSelect from "features/admin/group/GroupSelect";
import TelegramAppHeader from "common/Home/Header/TelegramAppHeader";
import { useHistory } from "react-router-dom";
import CreateBotButton from "common/Home/Header/CreateBotButton.tsx";
import useMeasure from "utils/hooks/useMeasure.ts";
import { PageWrapperFiltersContext } from "common/Home/PageWrapperFilters.context.ts";

interface HeaderProps {
  toggleMobileMenu: () => void;
  user: AuthUser;
  hideMobileFilters: boolean;
  banners?: ReactNode;
}

const Header = ({
  toggleMobileMenu,
  user,
  hideMobileFilters,
  banners,
}: HeaderProps) => {
  const { useMobileFilters, useMobileSearch } = useContext(PageWrapperContext);
  const { filters } = useContext(PageWrapperFiltersContext);
  const history = useHistory();
  const isSmallScreen = useMedia([`(max-width: 1000px)`], [true], false);
  const isMobileMenu = useMedia([`(max-width: ${MOBILE_MENU})`], [true], false);
  const isLargeMobile = useMedia(["(max-width: 750px)"], [true], false);
  const bottomNavigatorData = useBottomNavigator();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const [searchOpen, setSearchOpen] = useState(false);
  const isOnboarding = useIsOnboarding();
  const hideIcons = user.isLimitedAccess() || isOnboarding || IS_MOBILE_WEBVIEW;
  const hideTopSymbols =
    useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false) || hideIcons;
  const isDarkMode = useSelector(selectIsDarkMode);
  const workspace = useSelector(selectWorkspace);
  const isSettingsPath = useRoutesMatch(
    [SETTINGS_PATH, AFFILIATION_PATH],
    false
  );
  const isBillingPath = useRoutesMatch([BASE_BILLING_SETTINGS_PATH], false);
  const [titleMeasureRef, { width: titleWidth }] = useMeasure<HTMLDivElement>();
  const [measureRef, { width }] = useMeasure<HTMLDivElement>();
  const freeSpace = width - titleWidth - 32;

  const isDemoWorkspace = workspace === WorkspaceType.DEMO;
  const isAdminWorkspace = workspace === WorkspaceType.ADMIN;
  const hideOnboardingTitle =
    isOnboarding &&
    !isMobile &&
    !isSettingsPath &&
    !isBillingPath &&
    !isAdminWorkspace;
  const showFilters =
    useMobileFilters &&
    !hideMobileFilters &&
    filters &&
    !bottomNavigatorData.shouldDisplay;
  const showUserInfo =
    !IS_MOBILE_WEBVIEW &&
    user &&
    ((user.is_admin &&
      !user.is_onboarding &&
      !bottomNavigatorData.baseShouldDisplay) ||
      (!user.is_admin &&
        (!bottomNavigatorData.baseShouldDisplay || user.is_onboarding)));
  const showDemoTag = isDemoWorkspace && isOnboarding;
  const showWorkspaceSwitch =
    !isLargeMobile && !isOnboarding && (isBillingPath || !isSettingsPath);
  const showGroupSelect =
    workspace === WorkspaceType.ADMIN && !isMobileMenu && !isOnboarding;
  const showUpgradeButton =
    !isSmallScreen && !isOnboarding && (isDemoWorkspace || !user.isFree());
  const showBackButton =
    user.is_onboarding &&
    !user.isExpired() &&
    !isAdminWorkspace &&
    (isSettingsPath || isBillingPath);

  const mobileMenuButton = (
    <MenuOutlined
      style={{
        fontSize: HEADER_ICON_SIZE,
        marginTop: "auto",
        marginBottom: "auto",
      }}
      onClick={toggleMobileMenu}
    />
  );

  const renderLeftMobileMenuButton = () => {
    if (
      !IS_MOBILE_WEBVIEW &&
      !searchOpen &&
      isMobileMenu &&
      !user.is_onboarding &&
      !bottomNavigatorData.baseShouldDisplay
    ) {
      return mobileMenuButton;
    }
  };

  const onBackButtonClick = () => {
    history.goBack();
  };

  const renderRightAdminMenuButton = () => {
    if (user.is_admin && user.is_onboarding) {
      return mobileMenuButton;
    }
  };

  return (
    <HeaderWrapper>
      <TelegramAppHeader />
      {banners}
      <LayoutHeader
        $isMobileMenu={isMobileMenu}
        $isMobile={isMobile}
        $isOnboarding={isOnboarding}
      >
        <IconsWrapper>
          {renderLeftMobileMenuButton()}
          {useMobileSearch && (
            <MobileSearch
              searchOpen={searchOpen}
              setSearchOpen={setSearchOpen}
            />
          )}
          {user.is_onboarding && !showBackButton && (
            <img
              src={isDarkMode ? cornixWhiteLogo : cornixLogo}
              style={{
                height: isMobile ? "60%" : "75%",
                marginRight: isMobile ? 12 : 20,
              }}
              alt={"logo"}
            />
          )}
          {showBackButton && (
            <ArrowLeftOutlined
              style={{ fontSize: 18, marginRight: 12 }}
              onClick={onBackButtonClick}
            />
          )}
          {showDemoTag && (
            <DemoTag
              isSmall={isMobile}
              isShort={true}
              style={{ marginRight: 12 }}
            />
          )}
        </IconsWrapper>
        {(!searchOpen || !hideTopSymbols) && (
          <TitleSymbolsWrapper ref={measureRef}>
            {!searchOpen && (
              <TitleWrapper ref={titleMeasureRef}>
                {!hideOnboardingTitle && (
                  <div style={{ overflow: "hidden" }}>
                    <PageTitle />
                  </div>
                )}
                {showWorkspaceSwitch && (
                  <WorkspaceSwitch isShort={true} style={{ marginLeft: 16 }} />
                )}
                {showGroupSelect && (
                  <GroupSelect style={{ margin: "0 16px" }} />
                )}
              </TitleWrapper>
            )}
            {!hideTopSymbols && <TopSymbolsList maxWidth={freeSpace} />}
          </TitleSymbolsWrapper>
        )}
        <IconsWrapper
          style={
            bottomNavigatorData.baseShouldDisplay && searchOpen
              ? { width: "100%" }
              : undefined
          }
        >
          {renderRightAdminMenuButton()}
          {showUpgradeButton && <UpgradeButton style={{ marginLeft: 12 }} />}
          {!isOnboarding && <CreateBotButton />}
          {!hideIcons && (
            <>
              <NotificationsButton iconSize={HEADER_ICON_SIZE} />
              {!bottomNavigatorData.shouldDisplay && (
                <DisplayCurrencyToggle
                  style={{ marginRight: showFilters ? 0 : 12 }}
                />
              )}
            </>
          )}
          {showFilters && <MobileFilters />}
          {showUserInfo && (
            <UserInfo
              user={user}
              toggleMobileMenu={
                bottomNavigatorData.baseShouldDisplay
                  ? toggleMobileMenu
                  : undefined
              }
            />
          )}
        </IconsWrapper>
      </LayoutHeader>
    </HeaderWrapper>
  );
};

export default React.memo(Header);

const HeaderWrapper = styled.div`
  height: auto;
  position: relative;
  background-color: ${(p) => p.theme.colors.bgColor};
  line-height: unset;
  border-bottom: ${(p) => p.theme.borders.lightest};
  padding: 0;
  @media (max-width: ${BOTTOM_NAVIGATOR}) {
    border-bottom: none;
  }
`;
export const LayoutHeader = styled.div<{
  $isMobile?: boolean;
  $isOnboarding?: boolean;
  $isMobileMenu?: boolean;
}>`
  position: relative;
  background-color: ${(p) => p.theme.colors.bgColor};
  height: ${(p) => (p.$isMobile ? MOBILE_HEADER_HEIGHT : HEADER_HEIGHT)}px;
  padding: 0 ${(p) => (p.$isMobileMenu ? "16px" : "20px")};
  display: flex;
  justify-content: ${(p) =>
    p.$isMobileMenu || p.$isOnboarding ? "space-between" : "unset"};
  line-height: unset;
  @media (max-width: ${BOTTOM_NAVIGATOR}) {
    padding: 0 12px;
    height: 48px;
  }
`;
const IconsWrapper = styled.div`
  display: flex;
  align-items: center;
  color: ${(p) => p.theme.colors.secondaryText};
  height: 100%;
`;
const TitleWrapper = styled(Flex)`
  justify-content: flex-start;
  margin-top: auto;
  margin-bottom: auto;
  overflow: hidden;
  width: fit-content;
  @media (max-width: ${BOTTOM_NAVIGATOR}) {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }
}
`;
const TitleSymbolsWrapper = styled(Flex)`
  width: 100%;
`;
